import { FC, useState } from 'react';

import { FormControlLabel, Radio, RadioGroup, Grid } from '@mui/material';
import { observer } from 'mobx-react';

import QaFetcher, { ConsentResponse } from 'fetchers/QaFetcher';

import { showToast } from 'utils/UserMessageUtils';

import { usePatientModel } from 'components/Patient/usePatientModel';
import { Modal } from 'components/UIkit/atoms/Modal';
import { Text } from 'components/UIkit/atoms/Text';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

export const QaSimulateConsentSmsModal: FC<Props> = observer(({ isOpen, onClose, onSubmit }) => {
  const patient = usePatientModel();
  const [response, setResponse] = useState<ConsentResponse>(ConsentResponse.YES);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!patient?.id) {
      showToast({
        message: 'You must be on a patient page to use this feature'
      });
      return;
    }

    setIsSubmitting(true);
    await QaFetcher.simulateConsentSms({
      patientId: patient.id,
      response
    });

    showToast({
      message: `Successfully simulated patient response: ${response}`
    });
    onSubmit();
    setIsSubmitting(false);
  };

  const patientNameAndMrn = `${patient?.fullName} ${patient?.mrn ? `, MRN: ${patient.mrn}` : ''}`;

  return (
    <Modal
      isOpen={isOpen}
      title="Simulate Consent SMS"
      closeAction={{
        disabled: isSubmitting,
        onClick: onClose
      }}
      confirmActions={[
        {
          onClick: handleSubmit,
          text: 'Submit',
          disabled: isSubmitting || !patient?.id
        }
      ]}
    >
      <Grid container flexDirection="column" pb={24}>
        {patient?.id ? (
          <>
            <Grid container my={24} flexWrap="wrap" flexGrow={1}>
              <Text variant="h3">
                Simulate SMS response for: <strong>{patientNameAndMrn}</strong>
              </Text>
            </Grid>
            <Grid container mt={24} flexWrap="wrap" flexGrow={1}>
              <Text variant="body1" mr={24}>
                Select response:
              </Text>
              <RadioGroup
                value={response}
                onChange={(e) => setResponse(e.target.value as ConsentResponse)}
                row
              >
                <FormControlLabel value={ConsentResponse.YES} control={<Radio />} label="Yes" />
                <FormControlLabel value={ConsentResponse.NO} control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
          </>
        ) : (
          <Text>Simulate consent SMS must be done on the desired patient's page</Text>
        )}
      </Grid>
    </Modal>
  );
});
